// Taxonomie du projet : la liste des tags ADMIS pour ranger les verbatims.
//
// Dès qu'elle contient un tag, l'extraction IA n'étiquette plus qu'avec elle
// (le serveur écarte tout mot-clé inventé). Renommer ou fusionner un tag
// réécrit les verbatims ; le retirer de la taxonomie ne les touche pas — il
// redevient « hors taxonomie », où on peut l'adopter, le fusionner ou l'effacer.
//
// Gérer est réservé aux admins et Responsables (le serveur le garantit, 403) ;
// les autres membres lisent la taxonomie.
import { useId, useState, type FormEvent } from 'react';
import {
  useCreateTag,
  useDeleteTag,
  useMergeTags,
  useProjectTags,
  useStripTag,
  useUpdateTag,
} from '../api/queries';
import { ApiRequestError } from '../api/client';
import type { ProjectTag, UntrackedTag } from '../api/types';
import { Button, Chip, ErrorBanner, Eyebrow, Field, Note, Select, Spinner } from '../components/ui';
import { TagChip } from '../taxonomy/Chips';
import { hueOf } from '../taxonomy/colors';

function errorMessage(cause: unknown): string {
  if (cause instanceof ApiRequestError) return cause.message;
  return 'Une erreur inattendue est survenue.';
}

const plural = (n: number, one: string, many: string) => `${n} ${n > 1 ? many : one}`;
const verbatims = (n: number) => plural(n, 'verbatim', 'verbatims');
/** « Le verbatim prendra » / « Les 3 verbatims prendront » : sujet et verbe accordés. */
const theVerbatims = (n: number, one: string, many: string) =>
  (n > 1 ? `Les ${n} verbatims ${many}` : `Le verbatim ${one}`);

// Au-delà, la liste des tags hors taxonomie se replie : un projet importé
// avant la taxonomie en compte vite plusieurs dizaines.
const UNTRACKED_PREVIEW = 8;

const NO_FAMILY = 'Sans famille';

function groupByFamily(tags: ProjectTag[]): [string, ProjectTag[]][] {
  const groups = new Map<string, ProjectTag[]>();
  for (const t of tags) {
    const key = t.family ?? NO_FAMILY;
    groups.set(key, [...(groups.get(key) ?? []), t]);
  }
  // Les familles nommées d'abord, par ordre alphabétique ; « Sans famille » à la fin.
  return [...groups.entries()].sort(([a], [b]) =>
    a === NO_FAMILY ? 1 : b === NO_FAMILY ? -1 : a.localeCompare(b, 'fr'));
}

/** La taxonomie en écran à part entière, dans le menu de gauche. */
export function TaxonomyScreen({ projectId }: { projectId: string }) {
  return (
    <main className="screen">
      <div className="zy-page-head__texte">
        <Eyebrow>Projet</Eyebrow>
        <h1 className="t-h1">Taxonomie</h1>
        <p className="zy-intro">Les mots qui rangent les verbatims : ceux qu’on admet, ceux qu’on fusionne, ceux qui traînent.</p>
      </div>
      <TaxonomySection projectId={projectId} />
    </main>
  );
}

export function TaxonomySection({ projectId }: { projectId: string }) {
  const query = useProjectTags(projectId);
  const [notice, setNotice] = useState<string | null>(null);
  const [showAllUntracked, setShowAllUntracked] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [confirmBulk, setConfirmBulk] = useState(false);
  const bulk = useStripTag(projectId);

  const tags = query.data?.tags ?? [];
  const untracked = query.data?.untracked ?? [];
  // Sélection réellement valable : un tag adopté, fusionné ou supprimé entre-temps
  // n'est plus dans la liste, il sort de la sélection.
  const selected = untracked.filter((u) => picked.has(u.label)).map((u) => u.label);
  const allSelected = untracked.length > 0 && selected.length === untracked.length;
  const togglePick = (label: string) => setPicked((s) => {
    const n = new Set(s);
    if (n.has(label)) n.delete(label); else n.add(label);
    return n;
  });
  const canManage = query.data?.canManage ?? false;
  const families = [...new Set(tags.map((t) => t.family).filter((f): f is string => Boolean(f)))];
  const groups = groupByFamily(tags);
  const showGroups = groups.length > 1 || (groups.length === 1 && groups[0]![0] !== NO_FAMILY);

  return (
    <section className="zy-card opt-section" aria-labelledby="tax-title">
      <div className="opt-head">
        <div>
          <Eyebrow>Taxonomie</Eyebrow>
          <h2 className="t-h3" id="tax-title">Les tags du projet</h2>
          <p className="card-lede">
            Un vocabulaire commun pour ranger les verbatims. Dès qu’un tag y figure, l’IA n’étiquette plus
            qu’avec la taxonomie : elle n’invente plus de mots-clés.
          </p>
        </div>
      </div>

      {query.isPending ? (
        <Spinner label="Chargement de la taxonomie…" />
      ) : query.isError ? (
        <ErrorBanner>{errorMessage(query.error)}</ErrorBanner>
      ) : (
        <>
          {notice ? (
            <p className="tax-notice" role="status">
              {notice}
              <Button variant="link" size="s" type="button" onClick={() => setNotice(null)}>Fermer</Button>
            </p>
          ) : null}

          {tags.length === 0 ? (
            <p className="zy-texte-muet">
              Aucune taxonomie pour l’instant : l’IA propose librement 1 à 5 mots-clés par verbatim.
            </p>
          ) : (
            <div className="tax-groups">
              {groups.map(([family, list]) => (
                <div key={family} className="tax-group">
                  {showGroups ? <p className="opt-subtitle">{family} · {list.length}</p> : null}
                  <ul className="tax-list">
                    {list.map((t) => (
                      <TagRow
                        key={t.id}
                        projectId={projectId}
                        tag={t}
                        others={tags.filter((o) => o.id !== t.id)}
                        families={families}
                        canManage={canManage}
                        onNotice={setNotice}
                      />
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}

          {canManage ? (
            <AddTag projectId={projectId} families={families} onNotice={setNotice} />
          ) : (
            <Note tone="lecture">
              Lecture seule — la taxonomie est gérée par les responsables du projet et les administrateurs.
            </Note>
          )}

          {untracked.length > 0 ? (
            <div className="tax-untracked">
              <p className="opt-subtitle">Hors taxonomie · {untracked.length}</p>
              <p className="m0-note">
                Tags posés sur des verbatims mais absents de la taxonomie — souvent proposés par l’IA avant
                qu’elle existe. Adoptez-les, fusionnez-les dans un tag existant, ou supprimez-les.
              </p>

              {canManage ? (
                <div className="tax-bulk">
                  <label className="tax-pick">
                    <input className="zy-check"
                      type="checkbox"
                      checked={allSelected}
                      ref={(el) => { if (el) el.indeterminate = selected.length > 0 && !allSelected; }}
                      onChange={() => setPicked(allSelected ? new Set() : new Set(untracked.map((u) => u.label)))}
                    />
                    Tout sélectionner ({untracked.length})
                  </label>
                  {selected.length > 0 ? (
                    <Button variant="ghost" onClick={() => setConfirmBulk(true)}>
                      Supprimer la sélection ({selected.length})
                    </Button>
                  ) : null}
                </div>
              ) : null}

              {confirmBulk && selected.length > 0 ? (
                <div className="tax-confirm" role="alertdialog" aria-label="Confirmer la suppression">
                  <p className="zy-texte-muet">
                    {selected.length > 1 ? `Supprimer ces ${selected.length} tags ?` : `Supprimer « ${selected[0]} » ?`}{' '}
                    Ils disparaissent des verbatims qui les portent ; les verbatims eux-mêmes restent.
                  </p>
                  <div className="tax-actions">
                    <Button
                      variant="ghost"
                      loading={bulk.isPending}
                      onClick={() => bulk.mutate(selected, {
                        onSuccess: (r) => {
                          setPicked(new Set());
                          setConfirmBulk(false);
                          setNotice(`${plural(r.removed, 'tag supprimé', 'tags supprimés')} · ${verbatims(r.retagged)} mis à jour.`);
                        },
                      })}
                    >
                      Confirmer la suppression
                    </Button>
                    <Button variant="link" size="s" type="button" onClick={() => setConfirmBulk(false)}>Annuler</Button>
                  </div>
                  {bulk.error ? <ErrorBanner>{errorMessage(bulk.error)}</ErrorBanner> : null}
                </div>
              ) : null}

              <ul className="tax-list">
                {(showAllUntracked ? untracked : untracked.slice(0, UNTRACKED_PREVIEW)).map((u) => (
                  <UntrackedRow
                    key={u.label}
                    projectId={projectId}
                    item={u}
                    tags={tags}
                    canManage={canManage}
                    selected={picked.has(u.label)}
                    onToggle={() => togglePick(u.label)}
                    onNotice={setNotice}
                  />
                ))}
              </ul>
              {untracked.length > UNTRACKED_PREVIEW ? (
                <div>
                  <Button variant="ghost" onClick={() => setShowAllUntracked((v) => !v)}>
                    {showAllUntracked ? 'Replier' : `Voir les ${untracked.length - UNTRACKED_PREVIEW} autres`}
                  </Button>
                </div>
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}

// --- Un tag de la taxonomie ----------------------------------------------------

type RowMode = 'view' | 'edit' | 'merge' | 'delete';

function TagRow({ projectId, tag, others, families, canManage, onNotice }: {
  projectId: string;
  tag: ProjectTag;
  others: ProjectTag[];
  families: string[];
  canManage: boolean;
  onNotice: (s: string) => void;
}) {
  const [mode, setMode] = useState<RowMode>('view');
  const remove = useDeleteTag(projectId);

  if (mode === 'edit') {
    return (
      <li className="tax-row">
        <TagForm
          projectId={projectId}
          families={families}
          existing={tag}
          onDone={(msg) => { setMode('view'); if (msg) onNotice(msg); }}
        />
      </li>
    );
  }

  return (
    <li className="tax-row">
      <div className="tax-row-head">
        <div className="tax-who">
          <p className="tax-label"><TagChip tag={tag.label} /></p>
          {tag.description ? <p className="tax-desc">{tag.description}</p> : null}
        </div>
        <span className="tax-uses">{verbatims(tag.uses)}</span>
        {canManage && mode === 'view' ? (
          <div className="tax-actions">
            <Button variant="ghost" onClick={() => setMode('edit')}>Modifier</Button>
            {others.length > 0 ? (
              <Button variant="ghost" onClick={() => setMode('merge')}>Fusionner dans…</Button>
            ) : null}
            <Button variant="link-danger" size="s" type="button" title="Retirer de la taxonomie" onClick={() => setMode('delete')}>
              Retirer
            </Button>
          </div>
        ) : null}
      </div>

      {mode === 'merge' ? (
        <MergeInto
          projectId={projectId}
          sourceLabel={tag.label}
          uses={tag.uses}
          targets={others}
          request={{ sourceIds: [tag.id] }}
          onCancel={() => setMode('view')}
          onNotice={onNotice}
        />
      ) : null}

      {mode === 'delete' ? (
        <div className="tax-confirm">
          <p className="zy-texte-muet">
            {tag.uses > 0
              ? `${theVerbatims(tag.uses, 'garde', 'gardent')} ce tag : il réapparaîtra dans « Hors taxonomie ».`
              : 'Aucun verbatim ne porte ce tag.'}
          </p>
          <div className="tax-actions">
            <Button
              variant="ghost"
              loading={remove.isPending}
              onClick={() => remove.mutate(tag.id, {
                onSuccess: () => onNotice(`« ${tag.label} » retiré de la taxonomie.`),
              })}
            >
              Confirmer le retrait
            </Button>
            <Button variant="link" size="s" type="button" onClick={() => setMode('view')}>Annuler</Button>
          </div>
          {remove.error ? <ErrorBanner>{errorMessage(remove.error)}</ErrorBanner> : null}
        </div>
      ) : null}
    </li>
  );
}

// --- Fusion (commune aux tags de la taxonomie et aux tags hors taxonomie) -----

function MergeInto({ projectId, sourceLabel, uses, targets, request, onCancel, onNotice }: {
  projectId: string;
  sourceLabel: string;
  uses: number;
  targets: ProjectTag[];
  request: { sourceIds?: string[]; labels?: string[] };
  onCancel: () => void;
  onNotice: (s: string) => void;
}) {
  const merge = useMergeTags(projectId);
  const [targetId, setTargetId] = useState('');
  const target = targets.find((t) => t.id === targetId);
  const selectId = useId();

  return (
    <div className="tax-confirm">
      <div className="tax-merge-row">
        <label className="sr-only" htmlFor={selectId}>Fusionner « {sourceLabel} » dans</label>
        <Select id={selectId} className="tax-select" value={targetId} onChange={(e) => setTargetId(e.target.value)}>
          <option value="">Fusionner dans…</option>
          {targets.map((t) => (
            <option key={t.id} value={t.id}>{t.label}</option>
          ))}
        </Select>
        <Button
          variant="primary"
          disabled={!target}
          loading={merge.isPending}
          onClick={() => target && merge.mutate({ targetId: target.id, ...request }, {
            onSuccess: (r) => onNotice(
              `« ${sourceLabel} » fusionné dans « ${r.tag.label} »${r.retagged ? ` · ${verbatims(r.retagged)} mis à jour` : ''}.`,
            ),
          })}
        >
          Fusionner
        </Button>
        <Button variant="link" size="s" type="button" onClick={onCancel}>Annuler</Button>
      </div>
      {target ? (
        <p className="zy-texte-muet">
          {uses > 0 ? `${theVerbatims(uses, 'prendra', 'prendront')} le tag « ${target.label} ». ` : ''}
          {request.sourceIds?.length ? `« ${sourceLabel} » quittera la taxonomie.` : ''}
        </p>
      ) : null}
      {merge.error ? <ErrorBanner>{errorMessage(merge.error)}</ErrorBanner> : null}
    </div>
  );
}

// --- Un tag hors taxonomie -------------------------------------------------------

function UntrackedRow({ projectId, item, tags, canManage, selected, onToggle, onNotice }: {
  projectId: string;
  item: UntrackedTag;
  tags: ProjectTag[];
  canManage: boolean;
  selected: boolean;
  onToggle: () => void;
  onNotice: (s: string) => void;
}) {
  const [mode, setMode] = useState<'view' | 'merge' | 'strip'>('view');
  const adopt = useCreateTag(projectId);
  const strip = useStripTag(projectId);
  const others = item.variants.filter((v) => v !== item.label);

  return (
    <li className={`tax-row tax-row-untracked${selected ? ' tax-row-picked' : ''}`}>
      <div className="tax-row-head">
        <div className="tax-who">
          {canManage ? (
            <label className="tax-pick">
              <input className="zy-check" type="checkbox" checked={selected} onChange={onToggle} aria-label={`Sélectionner « ${item.label} »`} />
              <Chip hue={hueOf(item.label)} className="tax-label">{item.label}</Chip>
            </label>
          ) : (
            <p><Chip hue={hueOf(item.label)} className="tax-label">{item.label}</Chip></p>
          )}
          {others.length > 0 ? <p className="tax-desc">Aussi écrit : {others.join(', ')}</p> : null}
        </div>
        <span className="tax-uses">{verbatims(item.uses)}</span>
        {canManage && mode === 'view' ? (
          <div className="tax-actions">
            <Button
              variant="ghost"
              loading={adopt.isPending}
              onClick={() => adopt.mutate({ label: item.label }, {
                onSuccess: () => onNotice(`« ${item.label} » ajouté à la taxonomie.`),
              })}
            >
              Adopter
            </Button>
            {tags.length > 0 ? (
              <Button variant="ghost" onClick={() => setMode('merge')}>Fusionner dans…</Button>
            ) : null}
            <Button variant="link-danger" size="s" type="button" onClick={() => setMode('strip')}>
              Supprimer
            </Button>
          </div>
        ) : null}
      </div>
      {adopt.error ? <ErrorBanner>{errorMessage(adopt.error)}</ErrorBanner> : null}

      {mode === 'merge' ? (
        <MergeInto
          projectId={projectId}
          sourceLabel={item.label}
          uses={item.uses}
          targets={tags}
          request={{ labels: [item.label] }}
          onCancel={() => setMode('view')}
          onNotice={onNotice}
        />
      ) : null}

      {mode === 'strip' ? (
        <div className="tax-confirm">
          <p className="zy-texte-muet">
            Supprimer « {item.label} » ?{' '}
            {item.uses > 1 ? `Le tag disparaît des ${verbatims(item.uses)}` : 'Le tag disparaît du verbatim'} ;
            les verbatims eux-mêmes restent.
          </p>
          <div className="tax-actions">
            <Button
              variant="ghost"
              loading={strip.isPending}
              onClick={() => strip.mutate(item.label, {
                onSuccess: (r) => onNotice(`« ${item.label} » supprimé · ${verbatims(r.retagged)} mis à jour.`),
              })}
            >
              Confirmer la suppression
            </Button>
            <Button variant="link" size="s" type="button" onClick={() => setMode('view')}>Annuler</Button>
          </div>
          {strip.error ? <ErrorBanner>{errorMessage(strip.error)}</ErrorBanner> : null}
        </div>
      ) : null}
    </li>
  );
}

// --- Ajout et modification ------------------------------------------------------

function AddTag({ projectId, families, onNotice }: {
  projectId: string; families: string[]; onNotice: (s: string) => void;
}) {
  return (
    <div className="tax-add">
      <p className="opt-subtitle">Ajouter un tag</p>
      <TagForm projectId={projectId} families={families} onDone={(msg) => { if (msg) onNotice(msg); }} />
    </div>
  );
}

function TagForm({ projectId, families, existing, onDone }: {
  projectId: string;
  families: string[];
  existing?: ProjectTag;
  onDone: (notice: string | null) => void;
}) {
  const create = useCreateTag(projectId);
  const update = useUpdateTag(projectId);
  const [label, setLabel] = useState(existing?.label ?? '');
  const [family, setFamily] = useState(existing?.family ?? '');
  const [description, setDescription] = useState(existing?.description ?? '');
  const pending = create.isPending || update.isPending;
  const error = create.error ?? update.error;
  const renamed = existing && label.trim() && label.trim() !== existing.label;
  const listId = `tax-families-${existing?.id ?? 'new'}`;

  async function submit(e: FormEvent) {
    e.preventDefault();
    const body = { label: label.trim(), family: family.trim() || null, description: description.trim() || null };
    try {
      if (existing) {
        const r = await update.mutateAsync({ id: existing.id, ...body });
        onDone(r.retagged ? `« ${r.tag.label} » enregistré · ${verbatims(r.retagged)} mis à jour.` : null);
      } else {
        const r = await create.mutateAsync(body);
        setLabel('');
        setDescription('');
        onDone(`« ${r.tag.label} » ajouté${r.tag.uses ? ` · déjà posé sur ${verbatims(r.tag.uses)}` : ''}.`);
      }
    } catch {
      /* rendu par le bandeau */
    }
  }

  return (
    <form className="tax-form" onSubmit={submit} noValidate>
      <div className="tax-form-row">
        <Field label="Libellé *" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Saisie terrain" maxLength={40} voice={false} />
        <Field
          label="Famille"
          value={family}
          onChange={(e) => setFamily(e.target.value)}
          placeholder="Irritants, Parcours…"
          maxLength={60}
          voice={false}
          list={listId}
        />
        <datalist id={listId}>
          {families.map((f) => <option key={f} value={f} />)}
        </datalist>
      </div>
      <Field
        label="Définition"
        hint="Facultative — elle aide l’équipe et l’IA à choisir le bon tag."
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Ce que ce tag recouvre, et ce qu’il ne recouvre pas."
        maxLength={300}
      />
      {renamed && existing.uses > 0 ? (
        <p className="zy-texte-muet">{theVerbatims(existing.uses, 'prendra', 'prendront')} le nouveau libellé.</p>
      ) : null}
      {error ? <ErrorBanner>{errorMessage(error)}</ErrorBanner> : null}
      <div className="tax-actions">
        <Button type="submit" variant="accent" loading={pending} disabled={!label.trim()}>
          {existing ? 'Enregistrer' : 'Ajouter'}
        </Button>
        {existing ? (
          <Button variant="link" size="s" type="button" onClick={() => onDone(null)}>Annuler</Button>
        ) : null}
      </div>
    </form>
  );
}
